import { supabase } from '@/integrations/supabase/client';
import { AssessmentResults, AssessmentState, EditionType, ScreenConfig } from '@/types/assessment';
import { validateAssessmentResults } from './validateResults';
import { demandLabel, resilienceLabel } from './outlook';

export const saveResults = async (
  edition: EditionType,
  state: AssessmentState,
  results: AssessmentResults,
  userId?: string
) => {
  const { data, error } = await supabase
    .from('assessment_results')
    .insert({
      edition,
      selections: state.selections as any,
      open_texts: state.openTexts as any,
      results: results as any,
      user_id: userId || null,
    })
    .select('share_id')
    .single();

  if (error) {
    console.error('Error saving results:', error);
    return null;
  }
  return data.share_id;
};

/**
 * Maps optionId arrays → optionLabel arrays so the AI receives human-readable input.
 */
export const resolveSelectionsToLabels = (
  selections: Record<string, string[]>,
  screens: ScreenConfig[]
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  for (const [screenId, selectedIds] of Object.entries(selections)) {
    const screen = screens.find(s => s.id === screenId);
    if (!screen || !screen.options) {
      result[screenId] = selectedIds;
      continue;
    }
    result[screenId] = selectedIds.map(id => {
      const option = screen.options!.find(o => o.id === id);
      return option?.label ?? id;
    });
  }
  return result;
};

/**
 * Calls the Supabase generate-results edge function and returns validated AssessmentResults.
 * Throws on failure so the caller can fall back.
 */
const callGenerateResults = async (
  edition: EditionType,
  labeledSelections: Record<string, string[]>,
  openTexts: Record<string, string>,
  schoolId?: string | null,
  phase: string = 'full'
): Promise<Record<string, unknown>> => {
  const session = (await supabase.auth.getSession()).data.session;
  const controller = new AbortController();
  const timeoutMs = phase === 'profile' ? 60000 : 150000;
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-results`,
      {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ edition, selections: labeledSelections, openTexts, schoolId: schoolId ?? undefined, phase }),
      }
    );

    if (!response.ok) throw new Error(`Edge function error: ${response.status}`);
    const data = await response.json();
    if (data?.fallback) throw new Error('Edge function returned fallback');
    return data;
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * Two-phase generation:
 * Phase 1 (profile): youSummary + radarData + strengthClusters + path summaries (~15-20s)
 * Phase 2 (full): complete data with roadmaps, actions, skills (~50-60s)
 * Returns phase 1 results immediately. Caller can request phase 2 separately.
 */
export const generateResultsProfile = async (
  edition: EditionType,
  labeledSelections: Record<string, string[]>,
  openTexts: Record<string, string>,
  schoolId?: string | null
): Promise<AssessmentResults> => {
  const data = await callGenerateResults(edition, labeledSelections, openTexts, schoolId, 'profile');
  return validateAssessmentResults(data);
};

export const generateResultsFull = async (
  edition: EditionType,
  labeledSelections: Record<string, string[]>,
  openTexts: Record<string, string>,
  schoolId?: string | null
): Promise<AssessmentResults> => {
  const data = await callGenerateResults(edition, labeledSelections, openTexts, schoolId, 'full');
  return validateAssessmentResults(data);
};

// Backward-compatible: calls full by default
export const generateResults = async (
  edition: EditionType,
  labeledSelections: Record<string, string[]>,
  openTexts: Record<string, string>,
  schoolId?: string | null
): Promise<AssessmentResults> => {
  return generateResultsFull(edition, labeledSelections, openTexts, schoolId);
};

export const getShareUrl = (shareId: string) => {
  return `${window.location.origin}/shared/${shareId}`;
};

// Defensive: if the AI returns a lowercase or generic action-group key
// (e.g. "timeline" instead of "This Semester"), present it Title Cased so
// the PDF never shows a bare lowercase header.
const titleCaseTimeline = (key: string): string =>
  key
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const exportToPDF = async (results: AssessmentResults, configLabel: string, edition?: EditionType) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const youSummary = results.youSummary;
  const paths = results.paths;
  // High schoolers get "Projects to Build" (portfolio/initiative framing).
  // Monetization framing is only age-appropriate for college/professional.
  const isHighSchool = edition === 'highschool' || /high school/i.test(configLabel);
  const monetizeHeading = isHighSchool ? '🚀 Projects to Build' : '💰 Monetize Now';

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Your True Path · ${configLabel}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', system-ui, sans-serif; color: #1a1a2e; padding: 40px; max-width: 800px; margin: 0 auto; line-height: 1.6; }
        h1 { font-size: 28px; color: #2dd4a8; margin-bottom: 4px; }
        h2 { font-size: 20px; margin: 24px 0 12px; color: #1a1a2e; border-bottom: 2px solid #2dd4a8; padding-bottom: 4px; break-after: avoid; }
        h3 { font-size: 16px; margin: 16px 0 8px; color: #2dd4a8; break-after: avoid; }
        h4 { font-size: 14px; margin: 12px 0 6px; color: #555; break-after: avoid; }
        p { margin-bottom: 8px; font-size: 14px; color: #333; }
        .subtitle { color: #666; font-size: 14px; margin-bottom: 32px; }
        .section { margin-bottom: 20px; padding: 16px; background: #f8f9fa; border-radius: 8px; border-left: 3px solid #2dd4a8; }
        .path-section { margin-bottom: 24px; padding: 16px; background: #f0fdf4; border-radius: 8px; }
        .path-section.moderate { background: #fefce8; border-left: 3px solid #eab308; }
        .path-section.bold { background: #fef2f2; border-left: 3px solid #ef4444; }
        .path-section.conservative { border-left: 3px solid #2dd4a8; }
        ul { padding-left: 20px; margin-bottom: 8px; }
        li { font-size: 13px; margin-bottom: 4px; color: #444; }
        .badge { display: inline-block; padding: 2px 8px; background: #2dd4a8; color: white; border-radius: 12px; font-size: 11px; margin-right: 6px; }
        .footer { margin-top: 24px; text-align: center; color: #999; font-size: 12px; border-top: 1px solid #eee; padding-top: 16px; break-before: avoid; }
        /* break-inside: avoid on whole path-section caused tall sections to
           jump to the next page, leaving big blank gaps. Allow paths to
           split across pages; keep headings glued to their following
           content via break-after: avoid above. */
        @media print { body { padding: 20px; } .section { break-inside: avoid; } }
      </style>
    </head>
    <body>
      <h1>🧭 Your True Path</h1>
      <p class="subtitle">${configLabel} · Generated ${new Date().toLocaleDateString()}</p>

      <h2>Your Profile</h2>
      <div class="section"><h3>🧠 How Your Brain Works</h3><p>${youSummary.brainWiring}</p></div>
      <div class="section"><h3>⚡ Your Signature Strengths</h3><p>${youSummary.signatureStrengths}</p></div>
      <div class="section"><h3>🔥 What Lights You Up</h3><p>${youSummary.interests}</p></div>
      <div class="section"><h3>🧭 Where You're Headed</h3><p>${youSummary.direction}</p></div>
      <div class="section"><h3>🤖 AI Reality Check</h3><p>${youSummary.aiRealityCheck}</p></div>

      ${youSummary.cognitiveProfile ? `
      <div class="section">
        <h3>🧠 Cognitive Style: ${youSummary.cognitiveProfile.style}</h3>
        <p>${youSummary.cognitiveProfile.description}</p>
        <h4>Strengths</h4>
        <ul>${youSummary.cognitiveProfile.strengths.map(s => `<li>${s}</li>`).join('')}</ul>
        <h4>Blind Spots</h4>
        <ul>${youSummary.cognitiveProfile.blindSpots.map(s => `<li>${s}</li>`).join('')}</ul>
      </div>` : ''}

      <h2>Career Paths</h2>
      ${paths.map(path => `
        <div class="path-section ${path.riskLevel}">
          <h3>${path.name}</h3>
          <p>${path.description}</p>

          ${path.educationalPaths ? `
          <h4>🎓 Educational Options</h4>
          <ul>${path.educationalPaths.map(ep => `<li><strong>${ep.name}</strong> (${ep.duration}, ${ep.cost}): ${ep.bestFor}</li>`).join('')}</ul>
          ` : ''}

          ${Object.entries(path.actions).map(([timeline, actions]) => `
            <h4>${titleCaseTimeline(timeline)}</h4>
            <ul>${(actions as string[]).map(a => `<li>${a}</li>`).join('')}</ul>
          `).join('')}

          <h4>📚 Courses & Learning</h4>
          <ul>${path.skillsToStack.courses.map(c => `<li>${c}</li>`).join('')}</ul>

          <h4>${monetizeHeading}</h4>
          <ul>${path.monetizeNow.map(m => `<li>${m}</li>`).join('')}</ul>

          <h4>🌍 Global Outlook · ${path.globalOutlook.topRegion}</h4>
          <p>${demandLabel(path.globalOutlook.demandScore)} · Salary: ${path.globalOutlook.avgSalary} · ${resilienceLabel(path.globalOutlook.aiResilienceScore)}</p>
          <p>Trending: ${path.globalOutlook.trendingRoles.join(', ')}</p>
        </div>
      `).join('')}

      <div class="footer">
        <p>Generated by TruePath · Find Your True Path</p>
        <p>${window.location.origin}</p>
      </div>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
  setTimeout(() => printWindow.print(), 500);
};

// ─── Activities List Export (Common App format) ─────────────────────────────

export const exportActivitiesList = (results: AssessmentResults, configLabel: string) => {
  const path = results.paths?.[0]; // Use the primary (conservative) path
  const activities: string[] = [];

  // From counselor toolkit courses
  if (path?.counselorToolkit?.recommendedCourses) {
    path.counselorToolkit.recommendedCourses
      .filter((c: { priority: string }) => c.priority === 'must-take' || c.priority === 'recommended')
      .slice(0, 4)
      .forEach((c: { name: string; why: string; category: string }) => {
        activities.push(
          `ACADEMIC | ${c.name}\n` +
          `${c.why.slice(0, 150)}`
        );
      });
  }

  // From counselor toolkit clubs
  if (path?.counselorToolkit?.clubsAndOrgs) {
    path.counselorToolkit.clubsAndOrgs.slice(0, 4).forEach((club: { name: string; why: string; type: string }) => {
      const typeMap: Record<string, string> = {
        academic: 'ACADEMIC',
        leadership: 'LEADERSHIP',
        community: 'COMMUNITY SERVICE',
        creative: 'ART/MUSIC',
        competition: 'ACADEMIC',
        professional: 'CAREER ORIENTED',
      };
      activities.push(
        `${typeMap[club.type] || 'OTHER CLUB/ACTIVITY'} | ${club.name}\n` +
        `${club.why.slice(0, 150)}`
      );
    });
  }

  // From skills to stack
  if (path?.skillsToStack?.portfolio) {
    path.skillsToStack.portfolio.slice(0, 2).forEach((project: string) => {
      activities.push(
        `WORK (PAID) / INTERNSHIP | Personal Project\n` +
        `${project.slice(0, 150)}`
      );
    });
  }

  const output = [
    `═══ COMMON APP ACTIVITIES LIST ═══`,
    `Generated from: TruePath ${configLabel}`,
    `Date: ${new Date().toLocaleDateString()}`,
    ``,
    `Instructions: Copy each activity into a Common App slot.`,
    `Each description must be under 150 characters.`,
    `You have up to 10 slots. Customize and reorder by importance.`,
    ``,
    ...activities.map((a, i) => `── Activity ${i + 1} ──\n${a}\n`),
    ``,
    `═══ TIPS ═══`,
    `• Lead with your most impactful activity`,
    `• Use strong verbs: Led, Founded, Built, Organized, Designed`,
    `• Include numbers when possible: "Tutored 15 students weekly"`,
    `• Quality beats quantity. 3 deep activities beat 10 shallow ones`,
  ].join('\n');

  // Copy to clipboard and also offer download
  navigator.clipboard.writeText(output);
  const blob = new Blob([output], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `truepath-activities-list-${new Date().toISOString().split('T')[0]}.txt`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Counselor Summary Export ───────────────────────────────────────────────

export const exportCounselorSummary = (results: AssessmentResults, configLabel: string) => {
  const { youSummary } = results;
  const path = results.paths?.[0];

  const sections: string[] = [
    `═══ TRUEPATH COUNSELOR SUMMARY ═══`,
    `${configLabel} | Generated ${new Date().toLocaleDateString()}`,
    ``,
    `This summary is designed to help counselors understand this student's`,
    `strengths, interests, and career direction. It can inform recommendation`,
    `letters, course planning conversations, and college advising.`,
    ``,
    `── STUDENT PROFILE ──`,
    ``,
    `Brain Wiring: ${youSummary.brainWiring}`,
    ``,
    `Signature Strengths: ${youSummary.signatureStrengths}`,
    ``,
    `Interests: ${youSummary.interests}`,
    ``,
    `Direction: ${youSummary.direction}`,
    ``,
    `AI Readiness: ${youSummary.aiRealityCheck}`,
  ];

  if (youSummary.cognitiveProfile) {
    sections.push(
      ``,
      `── COGNITIVE STYLE ──`,
      `Style: ${youSummary.cognitiveProfile.style}`,
      `${youSummary.cognitiveProfile.description}`,
      `Strengths: ${youSummary.cognitiveProfile.strengths.join(', ')}`,
      `Growth Areas: ${youSummary.cognitiveProfile.blindSpots.join(', ')}`,
    );
  }

  if (results.strengthClusters?.length > 0) {
    sections.push(
      ``,
      `── STRENGTH CLUSTERS ──`,
      ...results.strengthClusters.map((c: { name: string; description: string; stat: string }) =>
        `• ${c.name}: ${c.description} (${c.stat})`
      ),
    );
  }

  if (path) {
    sections.push(
      ``,
      `── RECOMMENDED PATH: ${path.name} ──`,
      `${path.description}`,
    );

    if (path.counselorToolkit?.talkingPoints) {
      sections.push(
        ``,
        `── TALKING POINTS FOR COUNSELOR MEETING ──`,
        ...path.counselorToolkit.talkingPoints.map((tp: string) => `• ${tp}`),
      );
    }

    if (path.counselorToolkit?.recommendedCourses) {
      sections.push(
        ``,
        `── RECOMMENDED COURSES ──`,
        ...path.counselorToolkit.recommendedCourses.map((c: { name: string; priority: string; why: string; category: string }) =>
          `[${c.priority.toUpperCase()}] ${c.name} (${c.category}): ${c.why}`
        ),
      );
    }

    if (path.counselorToolkit?.clubsAndOrgs) {
      sections.push(
        ``,
        `── RECOMMENDED CLUBS & ORGANIZATIONS ──`,
        ...path.counselorToolkit.clubsAndOrgs.map((c: { name: string; type: string; why: string; commitment: string }) =>
          `• ${c.name} (${c.type}, ${c.commitment} commitment): ${c.why}`
        ),
      );
    }

    if (path.globalOutlook) {
      sections.push(
        ``,
        `── CAREER OUTLOOK ──`,
        `Average Salary: ${path.globalOutlook.avgSalary}`,
        `Growth Rate: ${path.globalOutlook.growthRate}`,
        `AI Resilience (estimate): ${resilienceLabel(path.globalOutlook.aiResilienceScore)}`,
        `Demand (estimate): ${demandLabel(path.globalOutlook.demandScore)}`,
        `Trending Roles: ${path.globalOutlook.trendingRoles.join(', ')}`,
      );
    }
  }

  sections.push(
    ``,
    `── FOR RECOMMENDATION LETTERS ──`,
    `Key phrases that describe this student:`,
    `• ${youSummary.cognitiveProfile?.style || 'Versatile thinker'} with strong ${results.strengthClusters?.[0]?.name?.toLowerCase() || 'analytical'} abilities`,
    `• Drawn to ${youSummary.interests.split('.')[0]?.trim() || 'interdisciplinary work'}`,
    `• ${youSummary.direction.split('.')[0]?.trim() || 'Career-focused'} student`,
    ``,
    `Generated by TruePath · ${window.location.origin}`,
  );

  const output = sections.join('\n');

  navigator.clipboard.writeText(output);
  const blob = new Blob([output], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `truepath-counselor-summary-${new Date().toISOString().split('T')[0]}.txt`;
  a.click();
  URL.revokeObjectURL(url);
};
